/**
 * Read the payload of a session JWT without verifying it.
 *
 * The signature is the server's business — the client only reads claims to decide
 * what to render, and every action they gate is re-authorized server side. Anything
 * malformed yields `null` rather than throwing, so a bad token degrades to "no claims"
 * instead of breaking session restoration.
 */
export function decodeJwtPayload<T = Record<string, unknown>>(jwt: string | null | undefined): T | null {
    if (!jwt) return null;

    const payloadSegment = jwt.split('.')[1];
    if (!payloadSegment) return null;

    try {
        const base64 = payloadSegment.replace(/-/g, '+').replace(/_/g, '/');
        const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
        const json = new TextDecoder().decode(Uint8Array.from(atob(padded), char => char.charCodeAt(0)));
        const payload = JSON.parse(json);
        return payload && typeof payload === 'object' ? (payload as T) : null;
    } catch {
        return null;
    }
}
